import {createContext, useCallback, useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {LoadingOverlay, Stack} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {notifications} from "@mantine/notifications";
import {chatApi} from "../chatApi";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import classes from "./ChatView.module.css";
import {Thread} from "./Thread";
import {
    addMessage,
    clearThreadError,
    removeMessagesFromIndex,
    selectActiveStreamCount,
    selectActiveThread,
    selectActiveThreadMessages,
    selectThreadByRealId,
    setActiveThread,
    setEphemeralSearch,
    setIsStreaming,
    setStreamId,
} from "../threadsSlice";
import {
    KAIResponseType,
    MessageWithMetadata,
    SeedMessage,
} from "../../../types";
import {ChatSplashView} from "../components/ChatSplashView";
import {
    nextPasteNumber,
    PendingPasteAttachment,
    readPasteAttachments,
    toPasteArtifacts,
    toRequestAttachments,
} from "../utils/pasteAttachments";
import {CitationPreviewProvider} from "../citation-preview/CitationPreviewContext";
import {CitationPreviewBox} from "../citation-preview/CitationPreviewBox";
import {WorkIslandProvider} from "../work-island/WorkIslandContext";
import {WorkIslandLayout} from "../work-island/WorkIslandLayout";
import {TopicIslandContent} from "../work-island/TopicIslandContent";
import {useWorkIslandUrlSync} from "../work-island/useWorkIslandUrlSync";
import {useHasRole} from "../../../featureFlags/featureFlags";
import {HasEditorPrivileges} from "../../../types";

const systemName = "DIB";
const MAX_CONCURRENT_STREAMS = 3;

const createEmptyMessage = (
    role: "user" | "assistant",
    position: number,
    responseType?: KAIResponseType
): MessageWithMetadata => {
    return {
        role,
        content: "",
        position,
        sources: [],
        relatedTools: [],
        isRegenerated: false,
        timestamp: new Date().toISOString(),
        thoughts: [],
        activities: [],
        pluginActivities: [],
        responseType,
    };
};

type ChatViewContextType = {
    regenerate: (
        messagePosition: number,
        responseType?: KAIResponseType
    ) => void;
    resend: (content: string, messagePosition: number) => void;
    sendMessage: (
        message: string,
        attachments?: PendingPasteAttachment[]
    ) => boolean;
    followUpQuestionSubmit: (question: string) => void;
};

export const ChatViewContext = createContext<ChatViewContextType>({
    regenerate: () => {},
    resend: () => {},
    sendMessage: () => false,
    followUpQuestionSubmit: () => {},
});

/**
 * Runs the island url restore inside WorkIslandProvider. The hook needs
 * useWorkIsland, so it must render below the Provider, not in ChatView.
 */
const WorkIslandUrlSync = ({messages}: {messages: MessageWithMetadata[]}) => {
    useWorkIslandUrlSync(messages);
    return null;
};

/** The island restores from the url only for editors and higher. */
const GatedWorkIslandUrlSync = ({messages}: {messages: MessageWithMetadata[]}) => {
    const {hasRole} = useHasRole(HasEditorPrivileges);
    if (!hasRole) return null;
    return <WorkIslandUrlSync messages={messages} />;
};

const ChatView = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {t} = useTranslation();

    const {threadId} = useParams<{threadId: string}>();
    const activeThread = useAppSelector(selectActiveThread);
    const messages = useAppSelector(selectActiveThreadMessages);
    const ephemeralSearch = activeThread?.ephemeralSearch ?? null;
    const activeStreamCount = useAppSelector(selectActiveStreamCount);

    const chosenResponseType = useAppSelector(
        (state) => state.session.chosenResponseType
    );

    const realThreadId = threadId === "new" ? undefined : threadId;

    // Keep activeThreadId in sync with the route
    const existingEntity = useAppSelector((state) =>
        realThreadId ? selectThreadByRealId(state, realThreadId) : undefined
    );
    const isLocalOnlyThread =
        !!existingEntity && existingEntity.realThreadId !== threadId;

    const {
        isFetching,
        isError: threadFetchErrored,
        isSuccess: threadFetchSucceeded,
        data: fetchedThread,
    } = chatApi.endpoints.getThread.useQuery(threadId || "", {
        refetchOnMountOrArgChange: true,
        skip: !threadId || threadId === "new" || isLocalOnlyThread,
    });

    // If the URL points to a thread we neither have locally nor can fetch
    // (e.g. the user reloaded on a never-synced local-only UUID), send them
    // back to the splash view rather than leaving them on an empty screen.
    useEffect(() => {
        if (!threadId || threadId === "new") return;
        if (existingEntity || isLocalOnlyThread) return;
        const missing =
            threadFetchErrored || (threadFetchSucceeded && !fetchedThread);
        if (missing) {
            navigate("/", {replace: true});
        }
    }, [
        threadId,
        existingEntity,
        isLocalOnlyThread,
        threadFetchErrored,
        threadFetchSucceeded,
        fetchedThread,
        navigate,
    ]);
    const existingEntityId = existingEntity?.id;
    const existingEntityRealThreadId = existingEntity?.realThreadId;
    useEffect(() => {
        if (!threadId) {
            dispatch(setActiveThread(null));
            return;
        }
        if (threadId === "new") {
            return;
        }
        if (existingEntityId) {
            dispatch(setActiveThread(existingEntityId));
        }
    }, [threadId, existingEntityId, dispatch]);

    // When the local-only thread finally receives its server-side id,
    // replace the URL so the route reflects the real thread id.
    useEffect(() => {
        console.log("[url-sync] effect", {
            threadId,
            existingEntityRealThreadId,
            pathname: window.location.pathname,
        });
        if (!threadId || threadId === "new") return;
        if (
            existingEntityRealThreadId &&
            existingEntityRealThreadId !== threadId
        ) {
            console.log("[url-sync] navigating", {
                from: threadId,
                to: existingEntityRealThreadId,
            });
            navigate(`/chat/${existingEntityRealThreadId}`, {replace: true});
        }
    }, [threadId, existingEntityRealThreadId, navigate]);

    const threadFiltered =
        !!activeThread?.error?.message?.includes("content_filter");

    const [sendMessage, {isLoading}] =
        chatApi.endpoints.sendMessage.useMutation();

    const ensureCapacity = useCallback(
        (currentThreadIsStreaming: boolean): boolean => {
            if (
                !currentThreadIsStreaming &&
                activeStreamCount >= MAX_CONCURRENT_STREAMS
            ) {
                notifications.show({
                    title: t("chat.multi_stream.capacity_title", {
                        defaultValue: "Too many active conversations",
                    }),
                    message: t("chat.multi_stream.capacity_message", {
                        defaultValue:
                            "3 conversations are already in progress. Please wait for one to finish.",
                    }),
                    color: "yellow",
                });
                return false;
            }
            return true;
        },
        [activeStreamCount, t]
    );

    const handleRegenerate = useCallback(
        (messagePosition: number, responseType?: KAIResponseType) => {
            if (!activeThread?.realThreadId) {
                return;
            }
            if (!ensureCapacity(activeThread.isStreaming)) return;

            const entityId = activeThread.id;
            const streamId = entityId;

            dispatch(clearThreadError({id: entityId}));
            dispatch(
                removeMessagesFromIndex({
                    id: entityId,
                    position: messagePosition,
                })
            );
            dispatch(
                addMessage({
                    id: entityId,
                    message: createEmptyMessage(
                        "assistant",
                        messagePosition,
                        responseType
                    ),
                })
            );
            dispatch(setIsStreaming({id: entityId, isStreaming: true}));
            dispatch(setStreamId({id: entityId, streamId}));

            sendMessage({
                system: {name: systemName},
                streamId,
                threadId: activeThread.realThreadId,
                messageId: messagePosition,
                responseType,
            });
        },
        [activeThread, dispatch, sendMessage, ensureCapacity]
    );

    const handleResend = useCallback(
        (content: string, messagePosition: number) => {
            if (!activeThread?.realThreadId) return;
            if (!ensureCapacity(activeThread.isStreaming)) return;

            const entityId = activeThread.id;
            const streamId = entityId;
            const resentArtifacts =
                messages.find((m) => m.position === messagePosition)
                    ?.artifacts ?? [];
            const pasteAttachments = readPasteAttachments(resentArtifacts);

            dispatch(clearThreadError({id: entityId}));
            dispatch(
                removeMessagesFromIndex({
                    id: entityId,
                    position: messagePosition,
                })
            );
            dispatch(
                addMessage({
                    id: entityId,
                    message: {
                        ...createEmptyMessage("user", messagePosition),
                        content,
                        ...(resentArtifacts.length && {
                            artifacts: resentArtifacts,
                        }),
                    },
                })
            );
            dispatch(
                addMessage({
                    id: entityId,
                    message: createEmptyMessage(
                        "assistant",
                        messagePosition + 1
                    ),
                })
            );
            dispatch(setIsStreaming({id: entityId, isStreaming: true}));
            dispatch(setStreamId({id: entityId, streamId}));

            sendMessage({
                system: {name: systemName},
                streamId,
                threadId: activeThread.realThreadId,
                messageId: messagePosition,
                message: content,
                attachments: pasteAttachments,
                metricsRunId: crypto.randomUUID(),
            });
        },
        [activeThread, messages, dispatch, sendMessage, ensureCapacity]
    );

    /** Returns `false` when the code does not accept the message. The input then keeps it. */
    const handleSendMessage = useCallback(
        (message: string, attachments: PendingPasteAttachment[] = []) => {
            if (!activeThread) return false;
            if (!ensureCapacity(activeThread.isStreaming)) return false;

            const entityId = activeThread.id;
            const streamId = entityId;
            const isEphemeralTransition =
                !!ephemeralSearch && !activeThread.realThreadId;

            const userPosition = messages.length > 0 ? messages.length : 0;
            const assistantPosition =
                messages.length > 0 ? messages.length + 1 : 1;

            dispatch(
                addMessage({
                    id: entityId,
                    message: {
                        ...createEmptyMessage("user", userPosition),
                        content: message,
                        ...(attachments.length && {
                            artifacts: toPasteArtifacts(
                                attachments,
                                nextPasteNumber(
                                    messages.flatMap((m) => m.artifacts ?? [])
                                )
                            ),
                        }),
                    },
                })
            );
            dispatch(
                addMessage({
                    id: entityId,
                    message: {
                        ...createEmptyMessage("assistant", assistantPosition),
                        responseType: chosenResponseType,
                    },
                })
            );
            dispatch(setIsStreaming({id: entityId, isStreaming: true}));
            dispatch(setStreamId({id: entityId, streamId}));

            let threadSeed: SeedMessage[] | undefined;
            if (isEphemeralTransition && ephemeralSearch) {
                threadSeed = [
                    {role: "user", content: ephemeralSearch.query},
                    {
                        role: "assistant",
                        content: "",
                        artifacts: [
                            {
                                type: "search_results",
                                payload: {
                                    query: ephemeralSearch.query,
                                    results: ephemeralSearch.results,
                                    searchId: ephemeralSearch.searchId,
                                    resultSetId: ephemeralSearch.resultSetId,
                                },
                            },
                        ],
                    },
                    {
                        role: "assistant",
                        content: t("chat.ephemeral_search.nudge"),
                    },
                ];
                dispatch(
                    setEphemeralSearch({id: entityId, ephemeralSearch: null})
                );
            }

            sendMessage({
                system: {name: systemName},
                streamId,
                threadId: activeThread.realThreadId,
                message,
                threadSeed,
                originSearchId: isEphemeralTransition
                    ? ephemeralSearch?.searchId
                    : undefined,
                originResultSetId: isEphemeralTransition
                    ? ephemeralSearch?.resultSetId
                    : undefined,
                attachments: toRequestAttachments(attachments),
                metricsRunId: crypto.randomUUID(),
                onThreadIdReceived: (newThreadId) => {
                    // Only swap the URL if the user is still on this thread's
                    // local path. If they've navigated elsewhere, don't yank
                    // them back.
                    if (window.location.pathname === `/chat/${entityId}`) {
                        navigate(`/chat/${newThreadId}`, {
                            replace: true,
                            state: {from: "/"},
                        });
                    }
                },
            });

            return true;
        },
        [
            navigate,
            activeThread,
            messages.length,
            chosenResponseType,
            dispatch,
            realThreadId,
            sendMessage,
            ephemeralSearch,
            t,
            ensureCapacity,
        ]
    );

    const handleFollowUpQuestionSubmit = useCallback(
        (question: string) => {
            handleSendMessage(question);
        },
        [handleSendMessage]
    );

    // Retained intentionally though now likely unreachable: ChatView only
    // mounts at /chat/:threadId (threadId always present) since the dashboard
    // — not ChatView — is the index route. New chats start via the dashboard
    // splash (DashboardSearchSection → TopSplashInput → useSplashSubmit).
    if (!threadId) {
        return <ChatSplashView />;
    }

    return (
        <ChatViewContext.Provider
            value={{
                regenerate: handleRegenerate,
                resend: handleResend,
                sendMessage: handleSendMessage,
                followUpQuestionSubmit: handleFollowUpQuestionSubmit,
            }}
        >
            <WorkIslandProvider key={activeThread?.id ?? realThreadId}>
            <GatedWorkIslandUrlSync messages={messages} />
            <CitationPreviewProvider>
                <WorkIslandLayout renderContent={(request, compactToc) => (
                    <TopicIslandContent key={request.source.url} source={request.source} compactToc={compactToc} />
                )}>
                <Stack className={classes.chatContainer} pos="relative">
                    <Thread
                        key={activeThread?.id ?? realThreadId}
                        id={realThreadId}
                        chatDisabled={isLoading || !!threadFiltered}
                    />
                    <LoadingOverlay
                        visible={
                            isFetching &&
                            activeThread?.realThreadId !== realThreadId
                        }
                        loaderProps={{type: "oval", color: "gneis.2", size: "md"}}
                    />
                    <CitationPreviewBox />
                </Stack>
                </WorkIslandLayout>
            </CitationPreviewProvider>
            </WorkIslandProvider>
        </ChatViewContext.Provider>
    );
};

export default ChatView;
